import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import PartyName from "./PartyName";
import { declareInterest, declineSubmission } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import { RESPONSES, canDecline, canDeclareInterest, formatDate } from "./placingUtils";
import NotesField from "./NotesField";
import { ReasonSelect, ReasonText } from "./ReasonFields";
import { useReasonCodes } from "../../hooks/useReasonCodes";
import { CodifiedDataButton } from "../codify/PlacementCodify";

// A decline reason is capped lower than notes
const MAX_REASON = 2000;

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Box fontSize="sm">{children}</Box>
  </Box>
);

/**
 * One party's response to the submission at build: declare interest (notes
 * optional) or decline to participate (reason required). Both name this
 * party, because a user acting for several carriers has one entry per party
 * (the API needs partyId then).
 */
const ResponseCard = ({ submissionId, entry, onChanged }) => {
  const toast = useToast();
  const [text, setText] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [busy, setBusy] = useState(null);
  const { isOther } = useReasonCodes();
  // Declining needs a reason code or some text, and text when the code is "other"
  const textRequired = !reasonCode || isOther(reasonCode);
  const response = RESPONSES[entry.responseCode];
  const mayDeclare = canDeclareInterest(entry);
  const mayDecline = canDecline(entry);

  const respond = async (kind) => {
    if (kind === "decline" && textRequired && !text.trim()) {
      return toast({ title: reasonCode ? "Explain the reason (you chose other)" : "Choose a reason or explain why you're declining", status: "warning" });
    }
    if (kind === "decline" && text.trim().length > MAX_REASON) {
      return toast({ title: `A reason can be at most ${MAX_REASON.toLocaleString()} characters`, status: "warning" });
    }
    setBusy(kind);
    try {
      if (kind === "decline") {
        await declineSubmission(submissionId, { reason: text.trim(), reasonCode, partyId: entry.partyId });
        toast({ title: "Declined to participate", status: "success" });
      } else {
        await declareInterest(submissionId, { notes: text.trim(), partyId: entry.partyId });
        toast({ title: "Interest declared", status: "success" });
      }
      setText("");
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: kind === "decline" ? "Couldn't decline" : "Couldn't declare interest" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
      <HStack justify="space-between" mb={2}>
        <Heading size="sm">Your response as <PartyName partyId={entry.partyId} /></Heading>
        {response ? (
          <Badge colorScheme={response.colour}>{response.label.toUpperCase()}</Badge>
        ) : (
          <PlacingStatusBadge status={entry.status} />
        )}
      </HStack>
      <HStack spacing={6} fontSize="sm" color="gray.600" flexWrap="wrap">
        {entry.sentDate && <Text>Sent {formatDate(entry.sentDate)}</Text>}
        {entry.viewedDate && <Text>Viewed {formatDate(entry.viewedDate)}</Text>}
        {entry.respondedDate && <Text>Responded {formatDate(entry.respondedDate)}</Text>}
        {!entry.respondedDate && entry.declinedDate && <Text color="red.600">Declined {formatDate(entry.declinedDate)}</Text>}
      </HStack>
      {entry.status === "declined" ? (
        <ReasonText fontSize="sm" mt={2} color="red.600" code={entry.reasonCode} notes={entry.responseNotes || entry.declineReason} />
      ) : (
        (entry.responseNotes || entry.declineReason) && (
          <Text fontSize="sm" mt={2} whiteSpace="pre-wrap" color="gray.700">{entry.responseNotes || entry.declineReason}</Text>
        )
      )}

      {(mayDeclare || mayDecline) && (
        <VStack align="stretch" mt={4} spacing={3}>
          <NotesField
            label={mayDeclare ? "Notes (needed to decline unless you choose a reason below)" : "Reason for declining"}
            value={text}
            onChange={setText}
          />
          <ReasonSelect label="Reason for declining (optional)" value={reasonCode} onChange={setReasonCode} maxW="420px" />
          <HStack>
            {mayDeclare && (
              <Button size="sm" colorScheme="teal" onClick={() => respond("interest")} isLoading={busy === "interest"} isDisabled={!!busy}>
                Declare interest
              </Button>
            )}
            <Button size="sm" colorScheme="red" variant="outline" onClick={() => respond("decline")} isLoading={busy === "decline"} isDisabled={!!busy}>
              Decline to participate
            </Button>
            <Text fontSize="xs" color="gray.500">as <PartyName partyId={entry.partyId} /></Text>
          </HStack>
        </VStack>
      )}
    </Box>
  );
};

/**
 * Carrier view of a submission sent to them. The API returns only the
 * user's own entries in sharedWithParties (one per party they act for),
 * never the other leads.
 */
const CarrierSubmissionView = ({ submission, onChanged }) => {
  const myEntries = submission.sharedWithParties || [];

  return (
    <VStack align="stretch" spacing={6}>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <Heading size="md">{submission.name || "Submission"}</Heading>
          <HStack>
            {/* Your own codification of the submission, once it has run */}
            <CodifiedDataButton placementId={submission.placementId} message={{ trigger: "submission", triggerId: submission._id }} size="xs" />
            <PlacingStatusBadge status={submission.status} />
          </HStack>
        </HStack>
        <SimpleGrid columns={[1, 2, 4]} spacing={3}>
          <Field label="Broker"><PartyName partyId={submission.sender?.partyId} /></Field>
          <Field label="Product">{submission.productCode || "—"}</Field>
          <Field label="Stage">{submission.placingStageCode || "—"}</Field>
          <Field label="Response required by">{formatDate(submission.responseRequiredByDate) || "—"}</Field>
          {submission.documentId && (
            <Field label="Contract document">
              <Link as={RouterLink} color="blue.600" to={`/editor?doc=${submission.documentId}`}>
                Open document
              </Link>
            </Field>
          )}
        </SimpleGrid>
        {submission.notes && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.500">Notes from the broker</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{submission.notes}</Text>
          </Box>
        )}
      </Box>

      {myEntries.map((entry) => (
        <ResponseCard key={entry.partyId} submissionId={submission._id} entry={entry} onChanged={onChanged} />
      ))}
    </VStack>
  );
};

export default CarrierSubmissionView;
